import React, { useState, useEffect } from 'react';
import { initSession, getSessionToken } from '../../services/sessionService';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Alert from '../ui/Alert';

interface InviteGateScreenProps {
  onSessionEstablished: () => void;
}

const InviteGateScreen: React.FC<InviteGateScreenProps> = ({ onSessionEstablished }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);

  // Check for existing session token on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const existingToken = getSessionToken();

      if (existingToken) {
        // Token exists - verify it's still valid by making a test call
        // For now, we'll just trust it and let the app handle expired tokens
        onSessionEstablished();
      } else {
        setIsCheckingExisting(false);
      }
    };

    checkExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (inviteCode.trim() === '') {
      setError('Please enter an invite code');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      await initSession(inviteCode.trim());
      onSessionEstablished();
    } catch (error) {
      console.error('Session initialization failed:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Invalid invite code. CRUX is currently in private beta.'
      );
      setIsValidating(false);
    }
  };

  // Show loading state while checking for existing session
  if (isCheckingExisting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-xl text-gray-300 mb-2">Loading CRUX...</div>
          <div className="text-sm text-gray-400">Checking session</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            <span className="text-6xl">C</span>RUX
          </h1>
          <p className="text-xl text-gray-200 mb-2">
            Private Beta Access
          </p>
          <p className="text-sm text-gray-300">
            Enter your invite code to continue
          </p>
        </div>

        {/* Invite Code Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="invite-code" className="block text-sm font-medium text-gray-200 mb-2">
              Invite Code
            </label>
            <Input
              id="invite-code"
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="Enter your invite code"
              disabled={isValidating}
              className="w-full"
              autoFocus
            />
          </div>

          {error && (
            <Alert variant="error">
              <p className="text-sm">{error}</p>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={isValidating || inviteCode.trim() === ''}
            className="w-full"
          >
            {isValidating ? 'Validating...' : 'Access CRUX'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default InviteGateScreen;
